import React from 'react';
import { arrayOf, bool, string, shape, instanceOf } from 'prop-types';
import { useHistory } from 'react-router-dom';
import styles from './styles/styles.module.scss';
import GiftCard from '../GiftCard';
import { PageContext } from '../../shared/Page/Page';
import { GiftFlowContext, RESTART_GIFT_FLOW } from '../GiftFlow/GiftFlow';
import { monthDayYear } from '../../../utils/time_helpers';
import Button from '../../shared/Button';
import { LanguageSupportUtil } from '../../../utils/language_support_util';
import { StringConstantsMixin, ProPageStrings } from '../../../application/javascripts/react_components/mixins/shared/string_constants_mixin';
import { pushHistory } from '../../../utils/client_side_routing_helpers';
import ShopifyPreview from '../../blocks/ShopifyPreview';
import { DESKTOP_BREAKPOINT } from '../../../utils/constants/breakpoints';

const {
  strings: {
    GIFT_FLOW: {
      GIFT_REVIEW: {
        GIFT_CARD_PURCHASED,
        THANK_YOU,
        PLAN,
        THANKS_FOR_GIVING,
        CHECK_FOR_CONFIRMATION,
        BUY_AGAIN,
        START_EXPLORING,
        DOWNLOAD_GIFT_CARD,
        CHECK_CONFIRMATION_AND_DOWNLOAD
      },
      GIFT_CUSTOMIZE_FORM: { TO, EMAIL, SEND_DATE }
    }
  }
} = StringConstantsMixin;

const {
  planTypeTitles: { annual, threeYr }
} = ProPageStrings;

const GiftCardAndMessage = ({ message, background }) => {
  return (
    <div className={styles.gcMessageContainer}>
      <GiftCard backgroundId={background} classes={styles.giftCard} />
      <div className={styles.giftMessage}>{message}</div>
    </div>
  );
};

const Info = ({ label, value }) => {
  return (
    <div className={styles.infoContainer}>
      <span className={styles.label}>{label}</span>: <span className={styles.value}>{value}</span>
    </div>
  );
};

const GiftReview = ({
  background,
  message,
  metadata,
  planType,
  recipientEmail,
  recipientName,
  isEmail,
  code,
  shopifyProducts,
  showShopifyPreview
}) => {
  const {
    globalState: {
      context: { languageRegionCode },
      windowWidth
    }
  } = React.useContext(PageContext);

  const { dispatchGiftFlow } = React.useContext(GiftFlowContext);
  const history = useHistory();

  const buyAgain = () => {
    dispatchGiftFlow({ type: RESTART_GIFT_FLOW });
    pushHistory('/gift', history, languageRegionCode);
  };

  const startExploring = () => {
    window.location = LanguageSupportUtil.wrapUrlSafe('/explore', languageRegionCode);
  };

  const downloadGiftCard = () => {
    pushHistory(`/gift/preview?${code}`, history, languageRegionCode);
  };

  return (
    <>
      <div className={styles.container}>
        <div className={styles.cardColumn}>
          <GiftCardAndMessage background={background} message={message} />
        </div>
        <div className={styles.metadataColumn}>
          <div className={styles.header}>
            <div>{GIFT_CARD_PURCHASED}</div>
            <div>{THANK_YOU}</div>
            {windowWidth && windowWidth < DESKTOP_BREAKPOINT && <GiftCardAndMessage background={background} message={message} />}
          </div>
          <div className={styles.metadata}>
            <div className={isEmail ? '' : styles.printStyles}>
              <Info label={TO} value={recipientName} />
              {isEmail && <Info label={EMAIL} value={recipientEmail} />}
              <Info label={PLAN} value={planType === 'annual' ? annual : threeYr} />
              {isEmail && <Info label={SEND_DATE} value={monthDayYear(metadata.deliveryDate, languageRegionCode)} />}
              {isEmail ? (
                <>
                  <div className={styles.spacer}>{THANKS_FOR_GIVING}</div>
                  <div className={styles.spacer}>{CHECK_FOR_CONFIRMATION}</div>
                </>
              ) : (
                <div className={styles.spacer}>{CHECK_CONFIRMATION_AND_DOWNLOAD}</div>
              )}
              {!isEmail && (
                <Button onClick={downloadGiftCard} variant="secondary" classes={styles.spacer}>
                  {DOWNLOAD_GIFT_CARD}
                </Button>
              )}
            </div>
            <Button onClick={buyAgain} variant="primary" classes={styles.spacer}>
              {BUY_AGAIN}
            </Button>
            <Button onClick={startExploring} variant="secondary" classes={styles.spacer}>
              {START_EXPLORING}
            </Button>
          </div>
        </div>
      </div>
      {showShopifyPreview && <ShopifyPreview shopifyProducts={shopifyProducts} windowWidth={windowWidth} page="gifting" />}
    </>
  );
};

GiftReview.propTypes = {
  background: string.isRequired,
  message: string,
  metadata: shape({
    deliveryDate: instanceOf(Date)
  }).isRequired,
  recipientEmail: string.isRequired,
  recipientName: string.isRequired,
  isEmail: bool,
  showShopifyPreview: bool,
  shopifyProducts: arrayOf(shape({ images: arrayOf(shape({ src: string, alt: string })), title: string, shopItemUrl: string, price: string }))
};

GiftReview.defaultProps = {
  isEmail: false,
  message: '',
  showShopifyPreview: false,
  shopifyProducts: []
};

export default GiftReview;
